import React, { useMemo } from 'react';
import { getLatestMetricValue } from '../../hooks/useDeviceHierarchy';
import { METRIC_CONFIG } from '../../../../constants/metricConfig';

interface Device {
  id: number;
  name: string;
  status: 0 | 1;
  socketConnected?: boolean;
  deviceTypes?: {
    id: number;
    name: string;
  };
}

interface USBDeviceCardProps {
  device: Device;
  deviceMetrics: Record<number, Record<string, any[]>>;
  themeColor?: string;
}

// USB/Fermentor specific metric display configuration
const USB_METRICS_CONFIG: Record<string, { label: string; unit: string; color: string; decimals?: number }> = {
  lt: { label: 'Level', unit: 'L', color: '#00bcd4', decimals: 1 },
  qt: { label: 'Flow Rate', unit: 'L/min', color: '#2196f3', decimals: 2 },
  ph: { label: 'pH', unit: '', color: '#9c27b0', decimals: 2 },
  tt: { label: 'Temperature', unit: '°C', color: '#ff9800', decimals: 1 },
  wt: { label: 'Weight', unit: 'kg', color: '#4caf50', decimals: 2 },
};

/**
 * Card component for USB sensor device metrics
 * Shows fermentor/bioprocess data like pH, temperature, level
 */
const USBDeviceCard: React.FC<USBDeviceCardProps> = ({
  device,
  deviceMetrics,
  themeColor = '#4caf50',
}) => {
  // Get available metrics
  const availableMetrics = useMemo(() => {
    const metricsData = deviceMetrics[device.id] || {};
    const allKeys = Object.keys(metricsData);
    return allKeys.filter(key => USB_METRICS_CONFIG[key] || METRIC_CONFIG[key]).slice(0, 6);
  }, [deviceMetrics, device.id]);

  return (
    <div style={{
      background: 'linear-gradient(135deg, #171a1c 0%, #1a1f22 100%)',
      border: `1px solid ${themeColor}30`,
      borderRadius: '12px',
      padding: '1rem',
      minWidth: '280px',
    }}>
      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem',
        paddingBottom: '0.75rem',
        borderBottom: `1px solid ${themeColor}20`,
      }}>
        <div style={{
          fontSize: '1.5rem',
        }}>
          🔌
        </div>
        <div style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          backgroundColor: device.socketConnected ? '#00ff88' : '#ff3366',
          boxShadow: device.socketConnected
            ? '0 0 8px #00ff88'
            : '0 0 8px #ff3366',
        }} />
        <div>
          <div style={{
            fontSize: '0.9rem',
            fontWeight: 600,
            color: '#fff',
          }}>
            {device.name}
          </div>
          <div style={{
            fontSize: '0.7rem',
            color: '#6c757d',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            {device.deviceTypes?.name || 'USB Sensor'}
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      {availableMetrics.length > 0 ? (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0.75rem',
        }}>
          {availableMetrics.map(key => {
            const usbConfig = USB_METRICS_CONFIG[key];
            const metricConfig = METRIC_CONFIG[key];
            const value = getLatestMetricValue(deviceMetrics, device.id, key) ?? 0;
            const decimals = usbConfig?.decimals ?? 2;
            const displayValue = typeof value === 'number'
              ? value.toFixed(decimals)
              : String(value);

            // Get display values from USB config first, then METRIC_CONFIG
            const label = usbConfig?.label || metricConfig?.title || key;
            const unit = usbConfig?.unit || metricConfig?.suffix || '';
            const color = usbConfig?.color || themeColor;

            return (
              <MetricBox
                key={key}
                label={label}
                value={displayValue}
                unit={unit}
                color={color}
              />
            );
          })}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          color: '#6c757d',
          fontSize: '0.85rem',
          padding: '1rem',
        }}>
          No metrics available
        </div>
      )}
    </div>
  );
};

interface MetricBoxProps {
  label: string;
  value: string;
  unit: string;
  color: string;
}

const MetricBox: React.FC<MetricBoxProps> = ({ label, value, unit, color }) => (
  <div style={{
    background: `${color}08`,
    border: `1px solid ${color}25`,
    borderRadius: '8px',
    padding: '0.6rem',
  }}>
    <div style={{
      fontSize: '0.6rem',
      color: `${color}`,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      marginBottom: '4px',
      fontWeight: 600,
    }}>
      {label}
    </div>
    <div style={{
      fontSize: '1rem',
      fontWeight: 700,
      color: '#fff',
      fontFamily: 'monospace',
    }}>
      {value}
      {unit && (
        <span style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '2px' }}>
          {unit}
        </span>
      )}
    </div>
  </div>
);

export default USBDeviceCard;
